import * as React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export interface MarkdownRendererProps {
  content: string;
  className?: string;
}

/**
 * DEV-95 — Read-only GFM-flavoured Markdown renderer. Shares styling with
 * MarkdownEditor so authored and rendered output look identical.
 *
 * The app-level Tailwind setup does not include @tailwindcss/typography, so
 * styling is done via a scoped `.markdown-body` CSS block. The `prose`
 * utilities are still applied (as a no-op today, useful later if the plugin
 * is added).
 */
export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  content,
  className = '',
}) => {
  return (
    <div className={`markdown-body prose prose-sm max-w-none ${className}`}>
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{content || ''}</ReactMarkdown>
      <style>{`
        .markdown-body h1 {
          font-size: 1.875rem;
          font-weight: 700;
          color: #111827;
          margin: 1rem 0 0.75rem;
          line-height: 1.2;
        }
        .markdown-body h2 {
          font-size: 1.5rem;
          font-weight: 600;
          color: #111827;
          margin: 1.25rem 0 0.6rem;
          line-height: 1.3;
        }
        .markdown-body h3 {
          font-size: 1.25rem;
          font-weight: 600;
          color: #111827;
          margin: 1rem 0 0.5rem;
        }
        .markdown-body p {
          color: #374151;
          line-height: 1.7;
          margin: 0.6rem 0;
        }
        .markdown-body strong { font-weight: 700; color: #111827; }
        .markdown-body em { font-style: italic; }
        .markdown-body ul {
          list-style: disc;
          padding-left: 1.5rem;
          margin: 0.6rem 0;
        }
        .markdown-body ol {
          list-style: decimal;
          padding-left: 1.5rem;
          margin: 0.6rem 0;
        }
        .markdown-body li { margin: 0.2rem 0; color: #374151; }
        .markdown-body blockquote {
          border-left: 3px solid #c4b5fd;
          padding-left: 0.875rem;
          color: #4b5563;
          font-style: italic;
          margin: 0.75rem 0;
        }
        .markdown-body code {
          background: #f3f4f6;
          padding: 0.1rem 0.3rem;
          border-radius: 0.25rem;
          font-size: 0.875em;
        }
        .markdown-body pre {
          background: #f3f4f6;
          padding: 0.75rem;
          border-radius: 0.5rem;
          overflow-x: auto;
          margin: 0.75rem 0;
        }
        .markdown-body pre code { background: transparent; padding: 0; }
        .markdown-body a {
          color: #2563eb;
          text-decoration: underline;
        }
        .markdown-body table {
          border-collapse: collapse;
          width: 100%;
          margin: 0.75rem 0;
        }
        .markdown-body th, .markdown-body td {
          border: 1px solid #e5e7eb;
          padding: 0.5rem 0.75rem;
          text-align: left;
        }
        .markdown-body th { background: #f9fafb; font-weight: 600; }
        .markdown-body hr {
          border: 0;
          border-top: 1px solid #e5e7eb;
          margin: 1.25rem 0;
        }
      `}</style>
    </div>
  );
};

export default MarkdownRenderer;
